import { useQuery } from '@apollo/client';
import axios from 'axios';
import { graphql, useStaticQuery } from 'gatsby';
import { head } from 'ramda';
import React, { FC, useState } from 'react';
import { useAsync, useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';

import FlexColumn from '@ui/atoms/FlexColumn';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';

import { keysToCamel } from '@utils/butter';
import { ApiEndpoints } from '@utils/endpoints';
import { doesNotExist, exists } from '@utils/general';

import { useAuth } from '@providers/AuthProvider';

import { IPageProps } from '@folx-types/flow-steps';
import {
	IAccountInfoQueryData,
	IDefaultMedicationDeliveryMethod,
	IDefaultMedicationDeliveryMethodResponse,
} from '@folx-types/member';

import currentMemberWithSubscriptionsQuery from '@graphql/queries/currentMemberWithSubscriptions.gql';
import defaultMedicationDeliveryMethod from '@graphql/queries/defaultMedicationDeliveryMethod.query.gql';

import MedicationDeliveryEditStep, {
	IMedicationDeliveryEditStepPageContent,
} from './MedicationDeliveryEditStep.component';

interface IMedicationDeliveryEditStepPageProps extends IPageProps {}

const FullWidthColumn = fullWidth(FlexColumn);
const AlignedColumn = aligned(FlexColumn);

const CardSkeleton = styled(Skeleton)`
	height: 32rem;
`;

const MedicationDeliveryEditStepPage: FC<IMedicationDeliveryEditStepPageProps> = ({ path }) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "medication-delivery-edit-step" }) {
				heading
				sub_heading
				capsule_disclaimer
				capsule_info_link_text
				capsule_info_link_url
				folx_shipping {
					shipping_header
					shipping_description
					membership_price
					membership_price_interval
					medication_price
					medication_price_interval
					enabled
				}
				capsule_delivery {
					capsule_header
					capsule_description
					membership_price
					membership_price_interval
					copay_price
					copay_price_description
					enabled
					capsule_logo
				}
				preferred_pharmacy {
					pharmacy_header
					pharmacy_description
				}
			}
		}
	`);

	const [loading, setLoading] = useState(true);
	const [currentDeliveryMethod, setCurrentDeliveryMethod] = useState<
		IDefaultMedicationDeliveryMethod | undefined
	>();

	const { loggedIn, initialized: authInitialized, getAuthToken } = useAuth();

	const {
		loading: subscriptionLoading,
		error: subscriptionError,
		data: subscriptionData,
	} = useQuery<IAccountInfoQueryData>(currentMemberWithSubscriptionsQuery, {
		skip: !loggedIn,
	});

	const {
		loading: defaultDeliveryMethodLoading,
		error: defaultDeliveryMethodError,
		data: defaultDeliveryMethodData,
	} = useQuery<IDefaultMedicationDeliveryMethodResponse>(defaultMedicationDeliveryMethod, {
		skip: !loggedIn,
	});

	const {
		loading: availableDeliveryMethodsLoading,
		error: availableDeliveryMethodsError,
		value: availableDeliveryMethods,
	} = useAsync<() => Promise<string[] | undefined>>(async () => {
		if (!authInitialized) {
			return;
		}

		const authorizationToken = await getAuthToken();
		const availableDeliveryMethodsData = await axios.get(
			`${ApiEndpoints.memberPortal}/available-rx-delivery-methods/`,
			{
				headers: {
					authorizationToken,
				},
				responseType: 'json',
			}
		);

		return availableDeliveryMethodsData?.data;
	}, [authInitialized]);

	useDeepCompareEffect(() => {
		if (doesNotExist(defaultDeliveryMethodData)) {
			return;
		}

		const currentDeliveryMethodNode = head(
			defaultDeliveryMethodData?.defaultMedicationDeliveryMethodForMember?.edges ?? []
		);
		setCurrentDeliveryMethod(currentDeliveryMethodNode?.node);
	}, [defaultDeliveryMethodData]);

	useDeepCompareEffect(() => {
		if (exists(subscriptionError)) {
			console.log(subscriptionError);
		}

		if (exists(defaultDeliveryMethodError)) {
			console.log(defaultDeliveryMethodError);
		}

		if (exists(availableDeliveryMethodsError)) {
			console.log(availableDeliveryMethodsError);
		}
	}, [subscriptionError, defaultDeliveryMethodError, availableDeliveryMethodsError]);

	useDeepCompareEffect(() => {
		const isLoading =
			availableDeliveryMethodsLoading || defaultDeliveryMethodLoading || subscriptionLoading;

		const dataExists =
			exists(subscriptionData?.currentMember) &&
			exists(currentDeliveryMethod) &&
			exists(availableDeliveryMethods);

		setLoading(isLoading || !dataExists);
	}, [
		availableDeliveryMethodsLoading,
		defaultDeliveryMethodLoading,
		subscriptionLoading,
		subscriptionData?.currentMember,
		currentDeliveryMethod,
		availableDeliveryMethods,
	]);

	const member = subscriptionData?.currentMember;

	const refinedContent: IMedicationDeliveryEditStepPageContent = keysToCamel(
		pageContent
	) as IMedicationDeliveryEditStepPageContent;

	const skeletonHeader = (
		<FlexColumn gap="md">
			<Skeleton height="lg" fullWidth />
			<Skeleton height="md" fullWidth />
		</FlexColumn>
	);

	const skeletonCards = (
		<FlexColumn gap="lg">
			<CardSkeleton fullWidth />
			<CardSkeleton fullWidth />
			<Skeleton structure="column" repeated={3} fullWidth height="sm" />
		</FlexColumn>
	);

	const skeletonButtons = (
		<AlignedColumn gap="lg" alignItems="center">
			<Skeleton fullWidth height="xl" />
			<Skeleton height="lg" width="xxxxl" />
		</AlignedColumn>
	);

	const skeleton = (
		<FullWidthColumn gap="xxl">
			{skeletonHeader}
			{skeletonCards}
			{skeletonButtons}
		</FullWidthColumn>
	);

	return (
		<LoadingWrapper loading={false} skeleton={skeleton}>
			<MedicationDeliveryEditStep
				pageContent={refinedContent}
				member={member}
				availableDeliveryMethods={availableDeliveryMethods}
				currentDeliveryMethod={currentDeliveryMethod?.medicationDeliveryMethod}
			/>
		</LoadingWrapper>
	);
};

export default MedicationDeliveryEditStepPage;
